import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDiaryStore } from '@/lib/diaryStore';
import { useAuthStore } from '@/features/auth/store';
import { useChambersMode } from '@/lib/useChambersMode';
import { useBarristerBusy } from '@/lib/useBarristerBusy';
import { nowLocalDatetime } from '@/lib/datetime';
import { BarristerDayTimeline } from '@/components/shared/BarristerDayTimeline';
import {
  X,
  CircleNotch,
  CalendarCheck,
  VideoCamera,
  MapPin,
  CheckCircle,
  Warning,
  Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  confirmConferenceSlot,
  FORMAT_LABELS,
  PURPOSE_LABELS,
  type Conference,
} from '@/lib/conferencesApi';
import { getApiErrorMessage } from '@/lib/api';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  conference: Conference;
  onClose: () => void;
  onConfirmed: (c: Conference) => void;
}

function fmtSlot(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

function isoLocalToUtc(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

const CLERK_TYPES = ['admin', 'senior_clerk', 'junior_clerk'] as const;
const BARRISTER_TYPES = ['silk', 'junior', 'pupil', 'door_tenant'] as const;

export function ConfirmSlotModal({ conference, onClose, onConfirmed }: Props) {
  const qc = useQueryClient();
  const fetchDiaryEvents = useDiaryStore((s) => s.fetchEvents);
  const user = useAuthStore((s) => s.user);
  const { isSolo } = useChambersMode();

  const userMemberType = user?.memberType ?? '';
  const isClerk = CLERK_TYPES.includes(userMemberType as typeof CLERK_TYPES[number]);
  const isBarrister = BARRISTER_TYPES.includes(userMemberType as typeof BARRISTER_TYPES[number]);
  // Solo barristers can schedule their own conferences
  const canConfirm = isClerk || (isSolo && isBarrister);

  // Derive the barrister's user ID — needed to fetch their schedule.
  // The conference may have been triggered by an allocation (brief), in which case
  // the barrister is on the allocation, otherwise it's the requestedBy user.
  const barristerId =
    conference.triggeringAllocation?.barrister.user.id ?? conference.requestedBy.id;
  const barFirstName =
    conference.triggeringAllocation?.barrister.user.firstName ?? conference.requestedBy.firstName;
  const barLastName =
    conference.triggeringAllocation?.barrister.user.lastName ?? conference.requestedBy.lastName;
  const barName = `${barFirstName} ${barLastName}`;

  const proposed = conference.proposedDates ?? [];

  const [selectedSlotIso, setSelectedSlotIso] = useState<string | null>(proposed[0] ?? null);
  const [customSlot, setCustomSlot] = useState('');
  const [duration, setDuration] = useState<number>(conference.durationMinutes);
  const [location, setLocation] = useState(conference.locationDetails ?? '');
  const [videoUrl, setVideoUrl] = useState(
    conference.format === 'video' ? conference.locationDetails ?? '' : '',
  );
  const [notes, setNotes] = useState('');

  const wantsCustom = !!customSlot;
  const finalSlotIso = wantsCustom ? isoLocalToUtc(customSlot) : selectedSlotIso;

  // All dates we need to check — proposed + any custom entry
  const allDatesToCheck = useMemo(() => {
    const dates = [...proposed];
    if (customSlot) dates.push(isoLocalToUtc(customSlot));
    return dates.filter(Boolean);
  }, [proposed, customSlot]);

  const { getConflicts, getEventsForDate, isLoading: busyLoading } = useBarristerBusy(
    barristerId,
    allDatesToCheck,
  );

  // Compute conflicts for each proposed slot (used for badges on radio buttons)
  const proposedConflicts = useMemo(
    () =>
      proposed.reduce<Record<string, ReturnType<typeof getConflicts>>>((acc, iso) => {
        acc[iso] = getConflicts(iso, duration);
        return acc;
      }, {}),
    [proposed, getConflicts, duration],
  );

  // Conflicts for the currently selected/custom slot
  const activeConflicts = useMemo(
    () => (finalSlotIso ? getConflicts(finalSlotIso, duration) : []),
    [finalSlotIso, duration, getConflicts],
  );

  // Events on the currently selected date (for the day timeline)
  const dayEvents = useMemo(
    () => (finalSlotIso ? getEventsForDate(finalSlotIso) : []),
    [finalSlotIso, getEventsForDate],
  );

  const hasBlockingConflict = activeConflicts.some((c) => c.severity === 'blocking');

  const confirmMut = useMutation({
    mutationFn: async () => {
      if (!finalSlotIso) throw new Error('Pick a slot or enter a custom datetime');
      const isVideo = conference.format === 'video';
      return confirmConferenceSlot(conference.id, {
        selectedAt: finalSlotIso,
        durationMinutes: duration,
        ...(isVideo && videoUrl ? { videoUrl } : {}),
        ...(!isVideo && location ? { location } : {}),
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: (result) => {
      toast.success('Conference scheduled — diary updated, all parties notified');
      qc.invalidateQueries({ queryKey: ['conferences'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['hearings'] });
      fetchDiaryEvents();
      onConfirmed(result.conference);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not confirm slot')),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !confirmMut.isPending) onClose();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <CalendarCheck size={15} weight="duotone" className="text-brand-600 dark:text-brand-400" />
              Schedule conference
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {conference.case.caseNumber} · {conference.case.caseName}
            </p>
          </div>
          <button
            type="button"
            onClick={() => !confirmMut.isPending && onClose()}
            disabled={confirmMut.isPending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Conference summary */}
          <div className="grid gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span><strong>Barrister:</strong> {barName}</span>
              <span><strong>Purpose:</strong> {PURPOSE_LABELS[conference.purpose]}</span>
              <span><strong>Format:</strong> {FORMAT_LABELS[conference.format]}</span>
              <span><strong>Lay client:</strong> {conference.layClientAttending ? 'attending' : 'not attending'}</span>
            </div>
            {conference.notes && (
              <div className="italic text-muted-foreground">"{conference.notes}"</div>
            )}
          </div>

          {/* ── Proposed slots (with conflict indicators) ── */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Proposed slots
              {proposed.length > 0 && (
                <span className="ml-1 font-normal normal-case">{proposed.length} suggested by barrister</span>
              )}
              {busyLoading && (
                <span className="ml-2 inline-flex items-center gap-1 font-normal normal-case text-muted-foreground/60">
                  <CircleNotch size={10} className="animate-spin" /> checking schedule…
                </span>
              )}
            </label>

            {proposed.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
                No slots proposed by the barrister. Use the custom datetime below.
              </div>
            ) : (
              <div className="space-y-1.5">
                {proposed.map((iso) => {
                  const slotConflicts = proposedConflicts[iso] ?? [];
                  const hasBlocking = slotConflicts.some((c) => c.severity === 'blocking');
                  const hasWarning = slotConflicts.some((c) => c.severity === 'warning');
                  const isSelected = !wantsCustom && selectedSlotIso === iso;

                  return (
                    <label
                      key={iso}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition ${
                        isSelected
                          ? hasBlocking
                            ? 'border-red-400 bg-red-50/50 dark:bg-red-500/10'
                            : hasWarning
                            ? 'border-amber-400 bg-amber-50/50 dark:bg-amber-500/10'
                            : 'border-brand-600 bg-brand-50'
                          : hasBlocking
                          ? 'border-red-200 dark:border-red-500/30 bg-red-50/20 dark:bg-red-500/10 hover:bg-red-50/40 dark:bg-red-500/10'
                          : hasWarning
                          ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50/20 dark:bg-amber-500/10 hover:bg-amber-50/40 dark:bg-amber-500/10'
                          : 'border-border bg-card hover:bg-muted/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="slot"
                        checked={isSelected}
                        onChange={() => { setSelectedSlotIso(iso); setCustomSlot(''); }}
                        disabled={confirmMut.isPending}
                        className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-brand-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{fmtSlot(iso)}</span>
                          {/* Conflict badge */}
                          {!busyLoading && (
                            hasBlocking ? (
                              <span className="flex items-center gap-1 rounded bg-red-100 dark:bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
                                <Warning size={9} weight="fill" /> Hearing conflict
                              </span>
                            ) : hasWarning ? (
                              <span className="flex items-center gap-1 rounded bg-amber-100 dark:bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-300">
                                <Warning size={9} weight="fill" /> Schedule conflict
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 rounded bg-emerald-100 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                                <CheckCircle size={9} weight="fill" /> Clear
                              </span>
                            )
                          )}
                        </div>
                        {/* Conflict details */}
                        {slotConflicts.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {slotConflicts.map((c) => (
                              <div key={c.event.id} className="text-xs text-muted-foreground">
                                <span className={c.severity === 'blocking' ? 'font-medium text-red-700 dark:text-red-300' : 'font-medium text-amber-700 dark:text-amber-300'}>
                                  {new Date(c.event.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  {' – '}
                                  {new Date(c.event.endAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {' '}
                                {c.event.title}
                                {c.event.case && (
                                  <span className="text-muted-foreground/60"> · {c.event.case.caseNumber}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Custom datetime ── */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Or pick a custom datetime
            </label>
            <Input
              type="datetime-local"
              value={customSlot}
              onChange={(e) => {
                setCustomSlot(e.target.value);
                if (e.target.value) setSelectedSlotIso(null);
              }}
              disabled={confirmMut.isPending}
              min={nowLocalDatetime()}
              step={60}
            />
            {wantsCustom && !busyLoading && activeConflicts.length === 0 && finalSlotIso && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                <CheckCircle size={10} weight="fill" /> Slot is clear — no existing commitments at this time
              </p>
            )}
            {!busyLoading && activeConflicts.length > 0 && finalSlotIso && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-red-600 dark:text-red-400">
                <Warning size={10} weight="fill" /> This time is already booked — please choose another.
              </p>
            )}
          </div>

          {/* ── Day schedule timeline (shown whenever a slot is active) ── */}
          {finalSlotIso && (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <Clock size={11} weight="duotone" />
                {barName}'s schedule on this day
              </div>
              <BarristerDayTimeline
                events={dayEvents}
                proposedStart={finalSlotIso}
                proposedDurationMinutes={duration}
                conflicts={activeConflicts}
              />
            </div>
          )}

          {/* ── Blocking conflict warning banner ── */}
          {hasBlockingConflict && (
            <div className="rounded-md border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-xs text-red-800 dark:text-red-300">
              <div className="flex items-center gap-1.5 font-semibold">
                <Warning size={13} weight="fill" />
                {barName} has a court hearing at this time
              </div>
              <p className="mt-1">
                Scheduling a conference during a hearing is not permitted under the Code of Conduct.
                Select a different time or confirm with the barrister first.
              </p>
            </div>
          )}

          {/* ── Duration + Location ── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                <Clock size={11} weight="duotone" />
                Duration (minutes)
              </label>
              <Input
                type="number"
                min={15}
                max={480}
                step={15}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value) || 60)}
                disabled={confirmMut.isPending}
              />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {conference.format === 'video'
                  ? <VideoCamera size={11} weight="duotone" />
                  : <MapPin size={11} weight="duotone" />}
                {conference.format === 'video' ? 'Video URL' : 'Location'}
              </label>
              <Input
                value={conference.format === 'video' ? videoUrl : location}
                onChange={(e) => {
                  if (conference.format === 'video') setVideoUrl(e.target.value);
                  else setLocation(e.target.value);
                }}
                placeholder={
                  conference.format === 'video'
                    ? 'https://meet.example.com/abc-123'
                    : conference.format === 'in_chambers'
                    ? 'Room 4, Ground Floor'
                    : conference.format === 'at_court'
                    ? 'Court 12, Royal Courts of Justice'
                    : 'Phone — number sent separately'
                }
                disabled={confirmMut.isPending}
              />
            </div>
          </div>

          {/* ── Notes ── */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Notes <span className="font-normal normal-case text-muted-foreground/70">(optional)</span>
            </label>
            <ExpandableTextarea
              label="Notes"
              value={notes}
              onChange={setNotes}
              placeholder="Agenda, papers needed, dial-in instructions…"
              rows={2}
              maxLength={2000}
              disabled={confirmMut.isPending}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-5 py-3">
          <div className="text-xs text-muted-foreground">
            {!canConfirm && (
              <span className="text-amber-700 dark:text-amber-300">Only clerks and admins can confirm slots</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={confirmMut.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => confirmMut.mutate()}
              disabled={confirmMut.isPending || !finalSlotIso || !canConfirm}
              className="gap-1.5"
            >
              {confirmMut.isPending ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <CheckCircle size={14} weight="fill" />
              )}
              {hasBlockingConflict ? 'Confirm anyway' : 'Confirm slot'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
